import { getEmployeeExperienceBaseUrl } from 'modules/api/employee-experience'

import {
  setShpLastBridgedEmployee,
  clearShpSessionOnIdentityChange,
  SHP_LAST_BRIDGED_EMPLOYEE_KEY,
} from './shopHealthSession'

const BRIDGE_LOGIN_PATH = '/api/v1/employee-experience/auth/bridge-login'

type EstablishParams = {
  idToken: string
  targetEmployeePublicKey?: string // set only when impersonating
  selfEmployeePublicKey?: string
  // Returns true if the session was invalidated (e.g. logout) after this mint was queued, so a
  // stale mint can bail before it re-establishes a session that has already been signed out.
  isStale?: () => boolean
}

/**
 * Mints/refreshes the EPS session cookie via a non-navigating bridge-login (a self token,
 * or an impersonation token when targetEmployeePublicKey is set; response body ignored).
 * On failure, clears the session only if a DIFFERENT employee is currently bridged — a
 * failed same-identity refresh leaves the still-valid cookie alone.
 */
export const establishEpsSession = async ({
  idToken,
  targetEmployeePublicKey,
  selfEmployeePublicKey,
  isStale,
}: EstablishParams): Promise<void> => {
  const bridgedEmployeeKey = targetEmployeePublicKey || selfEmployeePublicKey
  if (!idToken || !bridgedEmployeeKey) return
  // Invalidated before this queued mint even ran (e.g. logout): skip the network call and the
  // marker write entirely so we don't re-authenticate after sign-out.
  if (isStale?.()) return

  const url = `${getEmployeeExperienceBaseUrl()}${BRIDGE_LOGIN_PATH}`
  const body = targetEmployeePublicKey ? { targetEmployeePublicKey } : {}
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      throw new Error(`bridge-login failed with status ${response.status}`)
    }
    // Logout can land while this mint is in flight; the server has just re-set the cookie, so
    // undo it and skip the marker write rather than resurrecting the signed-out session.
    if (isStale?.()) {
      clearShpSessionOnIdentityChange()
      return
    }
    // Record the bridged identity (shared marker with the shop-health session).
    setShpLastBridgedEmployee(bridgedEmployeeKey)
  } catch (error) {
    console.error('Failed to establish EPS session:', error)
    // Clear only on a genuine identity mismatch — never on a same-identity refresh failure.
    const currentlyBridged = localStorage.getItem(SHP_LAST_BRIDGED_EMPLOYEE_KEY)
    if (currentlyBridged && currentlyBridged !== bridgedEmployeeKey) {
      clearShpSessionOnIdentityChange()
    }
  }
}

// Resolves after the most recent bridge-login mint settles (establishEpsSession never
// rejects), so cookie-guarded EPS calls (e.g. /me) can await the session instead of racing
// it. setupEpsSessionSync assigns the in-flight mint here; defaults to resolved pre-mint.
let epsSessionReady: Promise<void> = Promise.resolve()

/** Await the in-flight (or most recent) EPS session mint before a cookie-guarded call. */
export const whenEpsSessionReady = (): Promise<void> => epsSessionReady

type StoreLike = {
  getState: () => unknown
  subscribe: (listener: () => void) => () => void
}

// Selectors injected to avoid an import cycle (actions -> this helper -> selectors).
type EpsSessionSelectors = {
  getCredentials: (
    state: unknown
  ) => { idToken?: string; personPublicKey?: string } | null | undefined
  isImpersonating: (state: unknown) => boolean
  getUserId: (state: unknown) => string | undefined
}

/**
 * Keeps the EPS session cookie fresh for the whole session and clears it on logout.
 * Keyed on the acting identity (idToken + bridged employee), so it re-mints on token
 * refresh AND when the impersonation target resolves (e.g. a reload restoring userId),
 * and clears when the idToken disappears (any logout path).
 */
export const setupEpsSessionSync = (
  store: StoreLike,
  { getCredentials, isImpersonating, getUserId }: EpsSessionSelectors
): void => {
  let lastKey: string | undefined
  // Bumped on logout to invalidate any bridge-login that was queued or in flight beforehand, so
  // it can't re-mint the cookie/marker after the session has been cleared.
  let generation = 0

  const attempt = () => {
    const state = store.getState() || {}
    const credentials = getCredentials(state)
    const idToken = credentials?.idToken || undefined

    if (!idToken) {
      if (lastKey) {
        // present -> absent: logout
        lastKey = undefined
        generation += 1
        clearShpSessionOnIdentityChange()
      }
      return
    }

    const targetEmployeePublicKey = isImpersonating(state) ? getUserId(state) : undefined
    const selfEmployeePublicKey = credentials?.personPublicKey
    const bridgedEmployeeKey = targetEmployeePublicKey || selfEmployeePublicKey
    if (!bridgedEmployeeKey) return

    const key = `${idToken}|${bridgedEmployeeKey}`
    if (key === lastKey) return
    lastKey = key
    // Serialize bridge-logins so a later (target) call sets its cookie/marker after an earlier
    // (self) one — the cookie is server-set, so only ordering keeps the target cookie last.
    const mintGeneration = generation
    epsSessionReady = epsSessionReady.then(() =>
      establishEpsSession({
        idToken,
        targetEmployeePublicKey,
        selfEmployeePublicKey,
        isStale: () => generation !== mintGeneration,
      })
    )
  }

  store.subscribe(attempt)
  attempt()
}
